/**
 * The authentication seam.
 *
 * Two implementations exist: real Cognito (`./cognito.ts`, SRP via
 * `amazon-cognito-identity-js`, chosen when the pool is configured) and a local
 * fake (`./fake.ts`) for development without AWS. Nothing else in the app knows
 * how a session was obtained.
 *
 * In production the role arrives as the `custom:role` claim on the ID token —
 * a Cognito custom attribute stamped by `POST /auth/signup`, not a
 * pre-token-generation trigger (ADR 0006 decision 10) — alongside
 * `custom:org_id` and `custom:user_id`, which are the same three claims the
 * API's tenant middleware requires.
 *
 * Two rules this interface deliberately encodes:
 *
 *   1. **The client decides what to show; the server decides what to return.**
 *      `apps/README.md`, and invariant I-3. `Session.role` drives navigation and
 *      route visibility only. It is not, and must never become, an
 *      authorization decision — a UI that hides a field is not security.
 *   2. **The theme override is per account, not per browser.** DESIGN.md
 *      section 5. It therefore lives on the session and is written through this
 *      interface, so it follows the user to another machine once a real backend
 *      exists.
 */

import type { ThemePreference } from '../theme'
import type { FederatedProvider } from './federated'

/**
 * The two Cognito groups. ADR 0009.
 *
 * `provider_admin` is a customer account: a company that lists programs. It
 * keeps the name it has in the database and on the wire, because renaming a role
 * across a CHECK constraint, two Cognito groups and every token in circulation
 * buys a better word and nothing else.
 */
export const ROLES = ['provider_admin', 'mp_admin'] as const
export type Role = (typeof ROLES)[number]

export const ROLE_LABELS: Record<Role, string> = {
  provider_admin: 'Customer account',
  mp_admin: 'Match Point staff'
}

/**
 * Roles a self-service signup may choose. One, since ADR 0009.
 *
 * ADR 0004 section 6: `POST /auth/signup` is unauthenticated and lets the
 * caller pick a role, and `mp_admin` maps to a separate database role with
 * cross-tenant reads. The allowlist is enforced server-side; this constant
 * keeps the form from offering a choice the server will reject.
 *
 * Still a list with one member. The signup form no longer asks the question —
 * there is nothing to ask — but the thing this constant states is which roles a
 * stranger may claim, and that is worth keeping in one place for when a second
 * answer appears.
 */
export const SIGNUP_ROLES = ['provider_admin'] as const satisfies readonly Role[]
export type SignupRole = (typeof SIGNUP_ROLES)[number]

export function isRole (value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value)
}

/**
 * The org is the tenant (ADR 0002). A solo practitioner gets an org of one.
 * `orgId` is carried for display only — RLS derives the tenant from the token,
 * and per I-13 application code must not re-filter by tenant defensively.
 */
export interface Session {
  readonly userId: string
  readonly email: string
  readonly displayName: string
  readonly orgId: string
  readonly orgName: string
  readonly role: Role
  /** Per-account theme override. DESIGN.md section 5. */
  readonly themePreference: ThemePreference
}

export interface SignInInput {
  email: string
  password: string
}

export interface SignUpInput {
  email: string
  password: string
  displayName: string
  orgName: string
  role: SignupRole
  /** `ACC-05`: consent records the document version, not just a boolean. */
  acceptedLegalVersion: string
}

/**
 * Distinguishable from a network failure so the UI can say which happened.
 *
 * `fields` carries a validation problem's per-field messages, keyed the way
 * `toApiError` keys them: by the first segment of the RFC 6901 pointer, so
 * `/password` files under `password`. A screen puts them next to the control
 * they belong to instead of showing a banner.
 *
 * The `validation` code exists because sign-up used to have no way to say "your
 * request was fine to send and one field is wrong". Everything that was not a
 * duplicate email became `unavailable`, which told somebody with a short
 * password that the service was down and invited them to retry it.
 *
 * `not-confirmed` is the sign-in refusal for an account whose sign-up code was
 * never entered. It used to share `invalid-credentials`, which left the sign-in
 * screen able to say "enter the code we emailed you" and unable to offer the
 * one screen that takes it without matching on the message's wording.
 */
export class AuthError extends Error {
  constructor (
    message: string,
    readonly code: 'invalid-credentials' | 'not-confirmed' | 'duplicate-email' | 'validation' | 'unavailable',
    readonly fields?: Readonly<Record<string, string>>
  ) {
    super(message)
    this.name = 'AuthError'
  }
}

/**
 * What signing up produces.
 *
 * **Not a `Session`, and that is the change real Cognito forced.** This returned
 * `Promise<Session>` while the only implementation was a fake that could invent
 * one. A real account is `UNCONFIRMED` until the code from its sign-up email is
 * entered — and `ACC-02`'s done-when requires that email — so there is no
 * session to return. A signature promising one forces every honest
 * implementation to lie.
 */
/**
 * Where a federated sign-in landed (`ACC-06`, ADR 0012 decision 4).
 *
 * `needs-account` is somebody the provider vouched for who has no Match Point
 * account yet: their token carries none of the three claims, so nothing but
 * `POST /auth/federated-signup` will accept it. The screen asks for the two things
 * the provider does not know and calls `finishFederatedAccount`.
 */
export type FederatedOutcome =
  | { readonly kind: 'signed-in', readonly session: Session, readonly returnTo: string | null }
  | { readonly kind: 'needs-account', readonly email: string, readonly returnTo: string | null }
  /** Sent back to the provider for the one retry after linking; the browser is leaving. */
  | { readonly kind: 'redirecting' }

export interface FinishAccountInput {
  orgName: string
  displayName: string
}

export interface SignUpOutcome {
  readonly status: 'confirmation-required'
  readonly email: string
}

export interface AuthClient {
  /** Rehydrate on boot. Returns `null` when there is no session. */
  restore: () => Promise<Session | null>
  /**
   * The current Cognito ID token — the one API Gateway's authorizer validates —
   * or `undefined` when signed out. Called per request by the live `ApiClient`,
   * so a token refresh needs no re-wiring; the fake returns `undefined`, and
   * unauthenticated routes ignore it.
   */
  getIdToken: () => Promise<string | undefined>
  signIn: (input: SignInInput) => Promise<Session>
  signUp: (input: SignUpInput) => Promise<SignUpOutcome>
  /**
   * `ACC-02`'s second half: exchange the emailed code for a usable account.
   *
   * Separate from `signUp` because the two are separated by the user reading
   * their email, which may be minutes later and on another device.
   */
  confirmSignUp: (email: string, code: string) => Promise<void>
  /**
   * Send the confirmation code again.
   *
   * Codes expire, and mail goes astray. Without this a provider whose code has
   * aged out has no self-service way forward at all — the account exists,
   * unconfirmed, and signing up again returns "already exists".
   */
  resendConfirmationCode: (email: string) => Promise<void>
  /** `ACC-04`. Resolves whether or not the address exists — never confirm account existence. */
  requestPasswordReset: (email: string) => Promise<void>
  /**
   * `ACC-04`'s second half: exchange the emailed reset code for a new password.
   *
   * **This did not exist, and without it a reset could not be finished.** The
   * request above sends a numeric code — Cognito's `ForgotPassword` sends a code,
   * not a link — and nothing took it. Rejects with an `AuthError` whose message is
   * fit to show in the form.
   */
  confirmPasswordReset: (email: string, code: string, newPassword: string) => Promise<void>
  /**
   * Sends the browser to the provider. Null when this build cannot: the fake,
   * or a pool with no Cognito domain configured (`VITE_COGNITO_DOMAIN`).
   */
  federatedSignIn: ((provider: FederatedProvider, returnTo: string | null) => Promise<void>) | null
  /**
   * Sends the browser to Match Point's Entra tenant, on the staff pool (ADR 0016).
   * Null when this build has no staff pool (`VITE_STAFF_COGNITO_*`), and in the fake.
   *
   * A staff sign-in never needs the finish-account step: the first one provisions
   * the staff account itself, so the callback answers `signed-in` or an error.
   */
  staffSignIn: ((returnTo: string | null) => Promise<void>) | null
  /**
   * Whether the session in this browser came from the staff pool. The api client
   * asks per request, to send a staff session's calls to the staff API — API
   * Gateway refuses a staff token on the customer API, and the reverse (ADR 0016
   * decision 4). A transport decision, never an authorization one.
   */
  isStaffSession: () => boolean
  /** The callback's half: the code for tokens, and whether an account exists. */
  completeFederatedSignIn: (query: Readonly<Record<string, string | null>>) => Promise<FederatedOutcome>
  /** Creates the account for a `needs-account` outcome, then refreshes the session so it carries the claims. */
  finishFederatedAccount: (input: FinishAccountInput) => Promise<Session>
  signOut: () => Promise<void>
  /** Persists the per-account theme override and returns the updated session. */
  setThemePreference: (preference: ThemePreference) => Promise<Session>
  /**
   * Local development only. Re-issues the session as another role so the three
   * role gates can be exercised without three real accounts.
   *
   * Present on the interface rather than only on the fake so the shell's role
   * switcher can be typed; a real implementation returns `null` and the switcher
   * does not render. It is the honest shape: production cannot do this.
   */
  impersonateRole: ((role: Role) => Promise<Session>) | null
}
